import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook, TrendingUp, CheckCircle, Target, Zap, BarChart3, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";

const BlogPostCaseELEEVAIT = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="Como a ELEEVA IT Reduziu em 80% o Tempo de Atendimento"
                description="Case de sucesso: ELEEVA IT transformou sua operação de suporte com AROAN. Resultados: 80% de redução no tempo de atendimento, 85% de resolução no primeiro contato e 9.5/10 de satisfação do cliente."
                canonical="https://aroan.com.br/case-study/eleeva-it"
                ogImage="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop"
                author="Ana Costa"
                publishedTime="2024-11-03T00:00:00-03:00"
                keywords="case de sucesso, ELEEVA IT, redução de tempo de atendimento, ITSM, service desk, automação, ROI, satisfação do cliente"
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Case de Sucesso
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            Como a <span className="text-primary">ELEEVA IT</span> Reduziu em 80% o Tempo de Atendimento
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Ana Costa</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span>03 Nov, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span>10 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
                        <img
                            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop"
                            alt="ELEEVA IT Case Study"
                            className="w-full h-[400px] sm:h-[500px] object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Two Column Layout */}
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                            {/* Left Column */}
                            <div className="space-y-8">
                                {/* Introduction */}
                                <div className="prose prose-lg dark:prose-invert max-w-none">
                                    <h2 className="text-3xl font-bold mb-6 text-foreground">Introdução</h2>
                                    <p className="text-lg leading-relaxed">
                                        A ELEEVA IT, uma empresa de tecnologia em rápido crescimento, enfrentava desafios significativos em sua operação de suporte ao cliente. Com sistemas desconectados, processos manuais e falta de visibilidade, a equipe de suporte lutava para atender às crescentes demandas dos clientes, resultando em longos tempos de espera e baixa satisfação.
                                    </p>
                                    <p>
                                        A empresa precisava de uma solução que pudesse unificar suas operações, automatizar tarefas e fornecer a inteligência necessária para tomar decisões estratégicas. A implementação do AROAN, um sistema integrado de gestão inteligente, foi a resposta para esses desafios.
                                    </p>
                                </div>

                                {/* The Challenge */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Target className="w-6 h-6 text-red-500" />
                                        <h3 className="text-2xl font-bold text-foreground">O Desafio</h3>
                                    </div>
                                    <p className="text-muted-foreground mb-4">
                                        Antes do AROAN, a ELEEVA IT operava em um ambiente fragmentado, com vários sistemas que não se comunicavam. Isso criava silos de informação, dificultando a colaboração entre as equipes e o rastreamento de problemas.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="border-l-4 border-red-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">Processos Manuais e Lentos</h4>
                                            <p className="text-muted-foreground text-sm">
                                                A criação, o roteamento e a resolução de tickets eram feitos manualmente, resultando em atrasos e erros.
                                            </p>
                                        </div>
                                        <div className="border-l-4 border-orange-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">Falta de Visibilidade</h4>
                                            <p className="text-muted-foreground text-sm">
                                                A gestão não tinha uma visão clara do desempenho da equipe de suporte, dos principais problemas dos clientes ou do status dos ativos de TI.
                                            </p>
                                        </div>
                                        <div className="border-l-4 border-yellow-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">Base de Conhecimento Inexistente</h4>
                                            <p className="text-muted-foreground text-sm">
                                                A falta de uma base de conhecimento centralizada levava a um trabalho repetitivo, com a equipe de suporte resolvendo os mesmos problemas várias vezes.
                                            </p>
                                        </div>
                                        <div className="border-l-4 border-blue-500 pl-4">
                                            <h4 className="font-semibold text-foreground mb-2">Dificuldade no Rastreamento de Ativos</h4>
                                            <p className="text-muted-foreground text-sm">
                                                A empresa não conseguia rastrear seus ativos de TI de forma eficaz, o que dificultava a análise de impacto em caso de incidentes.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Results Table */}
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <BarChart3 className="w-6 h-6 text-green-600 dark:text-green-400" />
                                        <h3 className="text-2xl font-bold text-green-800 dark:text-green-200">Resultados Impressionantes</h3>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-green-200 dark:border-green-800">
                                                    <th className="text-left py-3 px-2 font-semibold text-green-800 dark:text-green-200">Métrica</th>
                                                    <th className="text-center py-3 px-2 font-semibold text-green-800 dark:text-green-200">Antes do AROAN</th>
                                                    <th className="text-center py-3 px-2 font-semibold text-green-800 dark:text-green-200">Depois do AROAN</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-sm">
                                                <tr className="border-b border-green-100 dark:border-green-900">
                                                    <td className="py-3 px-2 font-medium text-green-700 dark:text-green-300">Tempo médio de atendimento</td>
                                                    <td className="py-3 px-2 text-center text-red-600 dark:text-red-400">5 horas</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-bold">1 hora</td>
                                                </tr>
                                                <tr className="border-b border-green-100 dark:border-green-900">
                                                    <td className="py-3 px-2 font-medium text-green-700 dark:text-green-300">Resolução no primeiro contato</td>
                                                    <td className="py-3 px-2 text-center text-red-600 dark:text-red-400">40%</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-bold">85%</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-3 px-2 font-medium text-green-700 dark:text-green-300">Satisfação do cliente</td>
                                                    <td className="py-3 px-2 text-center text-red-600 dark:text-red-400">7.5/10</td>
                                                    <td className="py-3 px-2 text-center text-green-600 dark:text-green-400 font-bold">9.5/10</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                                        <div className="text-center p-4 bg-white dark:bg-green-900/30 rounded-lg">
                                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">80%</div>
                                            <div className="text-xs text-green-700 dark:text-green-300">Redução no Tempo de Atendimento</div>
                                        </div>
                                        <div className="text-center p-4 bg-white dark:bg-green-900/30 rounded-lg">
                                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">70%</div>
                                            <div className="text-xs text-green-700 dark:text-green-300">Redução no Trabalho Manual</div>
                                        </div>
                                        <div className="text-center p-4 bg-white dark:bg-green-900/30 rounded-lg">
                                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">85%</div>
                                            <div className="text-xs text-green-700 dark:text-green-300">Aumento na Velocidade de Resolução</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-8">
                                {/* The Solution */}
                                <div className="bg-card p-6 rounded-xl border border-border">
                                    <div className="flex items-center gap-3 mb-4">
                                        <CheckCircle className="w-6 h-6 text-primary" />
                                        <h3 className="text-2xl font-bold text-foreground">A Solução: Implementação do AROAN</h3>
                                    </div>
                                    <p className="text-muted-foreground mb-6">
                                        A ELEEVA IT implementou o AROAN para unificar e automatizar suas operações de suporte. A plataforma integrou a gestão de tickets, ativos, checklists e uma base de conhecimento inteligente, fornecendo uma solução completa para os desafios da empresa.
                                    </p>

                                    <div className="space-y-6">
                                        <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                                            <div className="flex items-start gap-3 mb-3">
                                                <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                                <h4 className="font-semibold text-blue-800 dark:text-blue-200">Gestão de Ativos</h4>
                                            </div>
                                            <p className="text-blue-700 dark:text-blue-300 text-sm">
                                                O AROAN forneceu uma visão do inventário completo e atualizado de todos os ativos de TI, com mapeamento de relacionamentos. Isso permitiu que a equipe de suporte entendesse o impacto de cada ativo nos serviços de negócio e resolvesse incidentes com mais rapidez.
                                            </p>
                                        </div>

                                        <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
                                            <div className="flex items-start gap-3 mb-3">
                                                <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                                                <h4 className="font-semibold text-green-800 dark:text-green-200">Workflows Automáticos Inteligentes</h4>
                                            </div>
                                            <p className="text-green-700 dark:text-green-300 text-sm">
                                                A empresa automatizou o roteamento de tickets, as aprovações e a criação de tarefas com os workflows do AROAN. Isso eliminou o trabalho manual, reduziu o tempo de resposta e garantiu que os problemas fossem direcionados para as equipes certas.
                                            </p>
                                        </div>

                                        <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/20">
                                            <div className="flex items-start gap-3 mb-3">
                                                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
                                                <h4 className="font-semibold text-purple-800 dark:text-purple-200">Base de Conhecimento Inteligente</h4>
                                            </div>
                                            <p className="text-purple-700 dark:text-purple-300 text-sm">
                                                A ELEEVA IT criou uma base de conhecimento centralizada com o AROAN, permitindo que a equipe de suporte encontrasse soluções para problemas conhecidos rapidamente. A integração com o portal de autoatendimento também permitiu que os clientes resolvessem seus próprios problemas, reduzindo o número de tickets.
                                            </p>
                                        </div>

                                        <div className="border border-orange-200 dark:border-orange-800 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
                                            <div className="flex items-start gap-3 mb-3">
                                                <Target className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
                                                <h4 className="font-semibold text-orange-800 dark:text-orange-200">Formulários Inteligentes</h4>
                                            </div>
                                            <p className="text-orange-700 dark:text-orange-300 text-sm">
                                                A empresa utilizou os formulários inteligentes do AROAN para coletar informações de forma estruturada e acionar processos automatizados. Isso simplificou a abertura de chamados e garantiu que a equipe de suporte tivesse todas as informações necessárias para resolver os problemas.
                                            </p>
                                        </div>

                                        <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 bg-indigo-50 dark:bg-indigo-900/20">
                                            <div className="flex items-start gap-3 mb-3">
                                                <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
                                                <h4 className="font-semibold text-indigo-800 dark:text-indigo-200">Dashboards e Relatórios Inteligentes</h4>
                                            </div>
                                            <p className="text-indigo-700 dark:text-indigo-300 text-sm">
                                                Com os dashboards do AROAN, a gestão da ELEEVA IT passou a ter visibilidade em tempo real de métricas críticas, como tempo de resposta, tempo de resolução e satisfação do cliente. Isso permitiu que a empresa identificasse gargalos e tomasse decisões baseadas em dados para melhorar continuamente seus processos.
                                            </p>
                                        </div>

                                        <div className="border border-pink-200 dark:border-pink-800 rounded-lg p-4 bg-pink-50 dark:bg-pink-900/20">
                                            <div className="flex items-start gap-3 mb-3">
                                                <Zap className="w-5 h-5 text-pink-600 dark:text-pink-400 mt-0.5 shrink-0" />
                                                <h4 className="font-semibold text-pink-800 dark:text-pink-200">VIA - O Assistente de IA</h4>
                                            </div>
                                            <p className="text-pink-700 dark:text-pink-300 text-sm">
                                                O assistente de IA do AROAN, VIA, forneceu sugestões inteligentes durante a resolução de tickets, analisou o conteúdo dos chamados e automatizou tarefas repetitivas, aumentando a produtividade da equipe de suporte.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Conclusion */}
                                <div className="bg-gradient-to-br from-primary/5 to-background border border-primary/10 rounded-2xl p-6">
                                    <h3 className="text-2xl font-bold mb-4 text-foreground">Conclusão</h3>
                                    <p className="text-muted-foreground mb-4">
                                        A implementação do AROAN foi um divisor de águas para a ELEEVA IT. A plataforma não apenas resolveu os desafios operacionais da empresa, mas também forneceu a base para uma cultura de melhoria contínua.
                                    </p>
                                    <p className="text-muted-foreground">
                                        Com o AROAN, a ELEEVA IT transformou sua operação de suporte de um centro de custos em um diferencial competitivo, aumentando a satisfação do cliente e impulsionando o crescimento do negócio.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Share Section */}
                        <div className="flex items-center justify-between border-y border-border py-6 mb-12 mt-12">
                            <span className="font-semibold">Compartilhe este case de sucesso:</span>
                            <div className="flex gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-600"
                                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Linkedin className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-400"
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("Confira este case de sucesso da ELEEVA IT com AROAN!")}`, '_blank')}
                                >
                                    <Twitter className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-700"
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Facebook className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: 'Case ELEEVA IT: 80% de Redução no Tempo de Atendimento',
                                                    text: 'Confira este case de sucesso da ELEEVA IT com AROAN!',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            alert("Link copiado para a área de transferência!");
                                        }
                                    }}
                                >
                                    <Share2 className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                                Quer resultados similares na sua empresa?
                            </h2>
                            <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                                Descubra como o AROAN pode transformar sua operação de suporte e reduzir drasticamente o tempo de atendimento, assim como fez com a ELEEVA IT.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#demo', '_self')}>
                                    Agende uma Demonstração
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/materiais', '_self')}>
                                    Baixe o Guia Gratuito
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostCaseELEEVAIT;
